// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required but not configured');
}

export interface AiSuggestionRequest {
  title: string;
  context?: string;
}

export interface HabitSuggestion {
  name: string;
  description: string;
  iconName: string;
  colorCode: string;
  frequency: 0 | 1 | 2 | 3; // Daily, WeeklySpecificDays, WeeklyCount, IntervalBased
  targetStreakLength: number;
  weeklyDays?: number[] | null;
  weeklyCount?: number | null;
  intervalDays?: number | null;
  reasoning: string;
}

export interface AiSuggestionResponse {
  success: boolean;
  message: string;
  suggestion: HabitSuggestion | null;
}

export interface AiLimitExceededResponse {
  success: boolean;
  message: string;
  usedToday: number;
  dailyLimit: number;
}

export class AiLimitExceededError extends Error {
  public readonly usedToday: number;
  public readonly dailyLimit: number;
  public readonly isAiLimitExceeded = true;

  constructor(usedToday: number, dailyLimit: number) {
    super(`Daily AI limit reached (${usedToday}/${dailyLimit}). Upgrade for more.`);
    this.usedToday = usedToday;
    this.dailyLimit = dailyLimit;
  }
}

export interface MultipleSuggestionsResponse {
  success: boolean;
  message: string;
  suggestions: HabitSuggestion[];
}

export interface DiscoverRequest {
  lifeAreas: string[];
  struggle: string;
  dailyTime: string;
  goal?: string;
}

export interface DiscoverResponse {
  success: boolean;
  message: string;
  suggestions: HabitSuggestion[];
}

class AiSuggestionApiService {
  private readonly baseUrl = `${API_BASE_URL}/aisuggestion`;

  private async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get token from localStorage for authenticated requests
    const token = localStorage.getItem('auth-store');
    let authToken = null;
    
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token;
      } catch (error) {
        console.error('Failed to parse auth token:', error);
      }
    }

    if (authToken) {
      defaultHeaders['Authorization'] = `Bearer ${authToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(endpoint, config);

      // Handle 429 Too Many Requests - AI limit exceeded
      if (response.status === 429) {
        try {
          const errorData = await response.json() as AiLimitExceededResponse;
          throw new AiLimitExceededError(errorData.usedToday, errorData.dailyLimit);
        } catch {
          throw new AiLimitExceededError(0, 5); // Default fallback
        }
      }

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.Message || errorMessage;
        } catch {
          // If response is not JSON, use the default error message
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof AiLimitExceededError) {
        throw error;
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  async getSuggestion(request: AiSuggestionRequest): Promise<HabitSuggestion> {
    const response = await this.makeAuthenticatedRequest<AiSuggestionResponse>(
      `${this.baseUrl}/suggest`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    
    if (!response.success || !response.suggestion) {
      throw new Error(response.message || 'Failed to generate suggestion');
    }
    
    return response.suggestion;
  }

  async getMultipleSuggestions(
    title: string, 
    context?: string, 
    count: number = 2
  ): Promise<HabitSuggestion[]> {
    const params = new URLSearchParams({
      title,
      count: count.toString(),
    });
    
    if (context) {
      params.append('context', context);
    }
    
    const response = await this.makeAuthenticatedRequest<MultipleSuggestionsResponse>(
      `${this.baseUrl}/suggest-multiple?${params.toString()}`
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to generate suggestions');
    }
    
    return response.suggestions;
  }

  async discoverHabits(request: DiscoverRequest): Promise<HabitSuggestion[]> {
    const response = await this.makeAuthenticatedRequest<DiscoverResponse>(
      `${this.baseUrl}/discover`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to generate habit suggestions');
    }

    return response.suggestions;
  }
}

export const aiSuggestionApiService = new AiSuggestionApiService(); 